"use client";
import { ReactNode } from "react";
import { ApolloLink, CombinedGraphQLErrors, CombinedProtocolErrors, HttpLink } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from "@apollo/client-integration-nextjs";
import { redirect } from "next/navigation";

import config from "@/lib/config";

const forwardCookieLink = new SetContextLink(async () => {
  // In SSR, map cookies to the headers field
  return import("next/headers").then(async ({ cookies }) => {
    const _cookies = await cookies();

    return {
      headers: {
        cookie: _cookies
          .getAll()
          .map(({ name, value }) => `${name}=${value}`)
          .join(";"),
      },
    };
  });
});

const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path, extensions }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
      if (extensions?.code === "NOT_SIGNED_IN") {
        redirect("/sign-out");
      }
    });
  } else if (CombinedProtocolErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) =>
      console.log(
        `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(
          extensions
        )}`
      )
    );
  } else {
    console.error(`[Network error]: ${error}`);
  }
});

// Horrible hack for SSR client components
// See: https://github.com/vercel/next.js/discussions/59303
const getMakeClient = () => {
  return () => {
    const httpLink: HttpLink = new HttpLink({
      uri: `${config.apiUrl}/graphql`,
      credentials: "include", // Web clients use built in cookie credentials
    });

    return new ApolloClient({
      cache: new InMemoryCache(),
      link:
        typeof window === "undefined"
          ? errorLink.concat(ApolloLink.from([
            new SSRMultipartLink({
              stripDefer: true,
            }),
            forwardCookieLink,
            httpLink,
          ]))
          : errorLink.concat(httpLink),
    });
  };
};

export function ApolloWrapper({ children }: { children: ReactNode }) {
  return (
    <ApolloNextAppProvider makeClient={getMakeClient()}>
      {children}
    </ApolloNextAppProvider>
  );
}

export default getMakeClient();
